/**
 * Identity Store - IndexedDB persistence for multiple poetic identities
 *
 * Design:
 * - Poetic Phrase: Deterministically derives UserId (private, never shared)
 * - NAME: Shareable identity name (like username, can be shared with others)
 * - PASSWORD: Backend authentication credential (not stored locally, sent to backend for verification)
 * - E2E Keys: Encrypted per-identity key storage
 */

import type { UserId } from '@maco144/shared';
import type { PoeticIdentity } from '@maco144/shared';

export interface StoredIdentity {
  userId: UserId;
  phrase: PoeticIdentity;
  name: string; // Shareable identity name (like "Alice", "luminous-phoenix", etc.)
  createdAt: number;
  lastUsedAt: number;
  homeRegion: string;
  groups: string[];
  // Password verification (simple hash, not for encryption)
  passwordHash: string; // SHA-256 hash of password for verification
  e2eKeysSalt: Uint8Array;
  encryptedE2EKeys: string; // JSON stringified and encrypted
}

export interface IdentityMetadata {
  activeUserId: UserId | null;
  version: number;
}

export class IdentityStore {
  private dbName = 'rising-sun-identities';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private metadataKey = '__metadata__';

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Identities store
        if (!db.objectStoreNames.contains('identities')) {
          const store = db.createObjectStore('identities', { keyPath: 'userId' });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('lastUsedAt', 'lastUsedAt', { unique: false });
        }

        // Metadata store (singleton)
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'id' });
        }
      };
    });
  }

  private getStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction(storeName, mode);
    return tx.objectStore(storeName);
  }

  /**
   * Save a new identity
   */
  async saveIdentity(identity: StoredIdentity): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('identities', 'readwrite').put(identity);
      request.onsuccess = () => {
        this.updateLastUsed(identity.userId)
          .then(() => resolve())
          .catch(reject);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a specific identity by userId
   */
  async getIdentity(userId: UserId): Promise<StoredIdentity | null> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('identities', 'readonly').get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * List all identities, sorted by lastUsedAt descending
   */
  async listIdentities(): Promise<StoredIdentity[]> {
    return new Promise((resolve, reject) => {
      const results: StoredIdentity[] = [];
      const request = this.getStore('identities', 'readonly').openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by lastUsedAt descending
          results.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update an identity (partial updates)
   */
  async updateIdentity(userId: UserId, updates: Partial<StoredIdentity>): Promise<void> {
    const existing = await this.getIdentity(userId);
    if (!existing) throw new Error(`Identity ${userId} not found`);

    const updated: StoredIdentity = {
      ...existing,
      ...updates,
      userId, // Never change the key
      createdAt: existing.createdAt, // Never change creation time
    };

    return new Promise((resolve, reject) => {
      const request = this.getStore('identities', 'readwrite').put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete an identity completely
   */
  async deleteIdentity(userId: UserId): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('identities', 'readwrite').delete(userId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update lastUsedAt timestamp
   */
  async updateLastUsed(userId: UserId): Promise<void> {
    const identity = await this.getIdentity(userId);
    if (identity) {
      return new Promise((resolve, reject) => {
        const request = this.getStore('identities', 'readwrite').put({
          ...identity,
          lastUsedAt: Date.now(),
        });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  }

  /**
   * Get active (currently logged-in) userId
   */
  async getActiveUserId(): Promise<UserId | null> {
    const metadata = await this.getMetadata();
    return metadata.activeUserId;
  }

  /**
   * Set the active userId
   */
  async setActiveUserId(userId: UserId | null): Promise<void> {
    const metadata = await this.getMetadata();
    await this.saveMetadata({
      ...metadata,
      activeUserId: userId,
    });
  }

  /**
   * Export identity with password re-encryption
   * Returns encrypted JSON that can be stored as backup
   */
  async exportIdentity(userId: UserId, newPassword: string): Promise<string> {
    const identity = await this.getIdentity(userId);
    if (!identity) throw new Error(`Identity ${userId} not found`);

    // Generate new salt for export
    const salt = crypto.getRandomValues(new Uint8Array(16));

    // Derive key from new password
    const key = await this.deriveKey(newPassword, salt);

    // Encrypt the identity data
    const plaintext = JSON.stringify(identity);
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(plaintext)
    );

    // Export format: {salt, iv, encrypted}
    const exportData = {
      salt: Array.from(salt),
      iv: Array.from(iv),
      encrypted: Array.from(new Uint8Array(encrypted)),
      version: 1,
    };

    return JSON.stringify(exportData);
  }

  /**
   * Import identity from encrypted backup
   */
  async importIdentity(data: string, password: string): Promise<StoredIdentity> {
    const exportData = JSON.parse(data);

    if (exportData.version !== 1) {
      throw new Error('Unsupported export version');
    }

    const salt = new Uint8Array(exportData.salt);
    const iv = new Uint8Array(exportData.iv);
    const encrypted = new Uint8Array(exportData.encrypted);

    // Derive key from password and salt
    const key = await this.deriveKey(password, salt);

    // Decrypt
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);

    const plaintext = new TextDecoder().decode(decrypted);
    const identity: StoredIdentity = JSON.parse(plaintext);

    // Verify it has all required fields
    if (!identity.userId || !identity.phrase) {
      throw new Error('Invalid identity data in export');
    }

    return identity;
  }

  /**
   * Clear all identities (for testing)
   */
  async clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['identities', 'metadata'], 'readwrite');
      tx.objectStore('identities').clear();
      tx.objectStore('metadata').clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    this.db?.close();
    this.db = null;
  }

  // ============ Private Helpers ============

  /**
   * Derive encryption key from password using PBKDF2
   */
  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as ArrayBuffer,
        iterations: 100000, // OWASP recommendation
        hash: 'SHA-256',
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Get or initialize metadata
   */
  private async getMetadata(): Promise<IdentityMetadata> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('metadata', 'readonly').get(this.metadataKey);
      request.onsuccess = () => {
        resolve(
          request.result || {
            activeUserId: null,
            version: 1,
          }
        );
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save metadata
   */
  private async saveMetadata(metadata: IdentityMetadata): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('metadata', 'readwrite').put({
        id: this.metadataKey,
        ...metadata,
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
